import { deleteAskInputDraftDatabase } from '#pplx/frontend/apps/ask/ask-input/ask-input-drafts/askInputDraftDatabase.js';
import { getCsrfToken } from '#pplx/frontend/auth/core.js';
import { deleteAccountStore } from '#pplx/frontend/browser-storage/accountScopedIDB.js';
import { clearAccountStorage } from '#pplx/frontend/browser-storage/accountStorage.js';
import { clearActiveAccountId, getActiveAccountId, } from '#pplx/frontend/browser-storage/activeAccount.js';
import { clearLastActiveAccountIfMatches } from '#pplx/frontend/browser-storage/lastActiveAccount.js';
import { EPPO_DB_NAME } from '#pplx/frontend/experimentation/IndexedDBStore.js';
import { logger } from '#pplx/frontend/logger/browser.js';
import { disablePush } from '#pplx/frontend/push-notifications/pushNotifications.js';
import { QUERY_CACHE_KEY } from '#pplx/frontend/react-query/consts.js';
import { authClient } from '#pplx_generated/typespec/rest/auth/client.js';
/**
 * Sign out a single linked account (multi-account web, plan PR 3.2).
 *
 * Clears the account's secondary session cookie server-side via the dedicated
 * `POST /api/auth/signout-account` endpoint (the generated `authClient`, with the next-auth CSRF
 * token in the body), then physically removes its local data — namespaced localStorage plus the
 * per-account React Query and Eppo IndexedDB databases — and drops it from the cross-tab
 * last-visible seed.
 *
 * Navigation is the caller's responsibility: if the signed-out account is *this tab's* active
 * account the tab must move off it (see `useLinkedAccounts`), which knows the remaining accounts
 * and can switch to one instead of stranding the tab on the now-cookieless account.
 */
export async function signOutAccount(accountId) {
    // Drop this account's web-push device row first, while its session cookie still resolves the
    // `X-Pplx-Account` attribution — otherwise the backend would keep pushing to it through the
    // browser's shared subscription. Best-effort (returns false / logs on its own).
    await disablePush({ accountId, reason: 'signOutAccount' });
    await clearAccountSessionCookie(accountId);
    await clearAccountLocalData(accountId, 'signOutAccount');
    clearLastActiveAccountIfMatches(accountId);
    // If this tab is currently acting as the signed-out account, drop its per-tab active pointer too.
    // The caller either redirects home (last account) — where the cleared pointer prevents sending a
    // now-cookieless `X-Pplx-Account` — or switches to another account, which re-sets it via
    // `?pplx_account=`. A tab acting as a different account (or the primary by fallback) is untouched.
    if (getActiveAccountId() === accountId) {
        clearActiveAccountId();
    }
}
async function clearAccountSessionCookie(accountId) {
    const { error, response } = await authClient.POST('/api/auth/signout-account', 'signOutAccount', {
        body: { csrfToken: await getCsrfToken(), account: accountId },
    });
    if (error) {
        logger.warn('signOutAccount: backend sign-out returned an error', { status: response.status });
    }
}
/**
 * Physically removes one account's local data: its namespaced localStorage plus its React Query,
 * Eppo and Ask input draft IndexedDB databases. Other accounts and global keys stay intact.
 * `source` names the caller in the failure log.
 */
export async function clearAccountLocalData(accountId, source) {
    clearAccountStorage(accountId);
    const [queryCacheDeleted, eppoDeleted, askInputDraftsDeleted] = await Promise.all([
        deleteAccountStore(QUERY_CACHE_KEY, accountId),
        deleteAccountStore(EPPO_DB_NAME, accountId),
        deleteAskInputDraftDatabase(accountId),
    ]);
    if (!queryCacheDeleted || !eppoDeleted || !askInputDraftsDeleted) {
        // Best-effort: the orphaned databases are inert without the session, but log so a recurring
        // failure to physically clear a signed-out account's data is trackable.
        logger.warn('signOutAccount: failed to delete account IndexedDB database', {
            source,
            queryCacheDeleted,
            eppoDeleted,
            askInputDraftsDeleted,
        });
    }
}
//# sourceMappingURL=signOutAccount.js.map